import { createContext, useContext, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const WishlistContext = createContext(null);
const LS_KEY = "wishlist";

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => {
    try {
      const stored = localStorage.getItem(LS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const hasHydratedFromServer = useRef(false);

  // Persist locally on every change
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(ids));
    } catch {
      /* ignore */
    }
  }, [ids]);

  // On login: merge local wishlist with server wishlist (union), then sync back
  useEffect(() => {
    if (!user) {
      hasHydratedFromServer.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/wishlist");
        if (cancelled) return;
        const serverItems = Array.isArray(r.data?.items) ? r.data.items : [];
        // Union local + server (preserve local order, append unseen server items)
        const merged = [...ids];
        for (const sid of serverItems) {
          if (!merged.includes(sid)) merged.push(sid);
        }
        setIds(merged);
        // Push merged back to server so subscriptions to alerts are up to date
        if (JSON.stringify(merged) !== JSON.stringify(serverItems)) {
          await api.post("/wishlist", { items: merged }).catch(() => null);
        }
        hasHydratedFromServer.current = true;
      } catch {
        hasHydratedFromServer.current = true;
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // After initial server hydration, push every mutation to server (debounced)
  useEffect(() => {
    if (!user || !hasHydratedFromServer.current) return;
    const t = setTimeout(() => {
      api.post("/wishlist", { items: ids }).catch(() => null);
    }, 400);
    return () => clearTimeout(t);
  }, [ids, user]);

  const has = (productId) => ids.includes(productId);
  const toggle = (productId) =>
    setIds((prev) =>
      prev.includes(productId)
        ? prev.filter((p) => p !== productId)
        : [...prev, productId]
    );
  const remove = (productId) =>
    setIds((prev) => prev.filter((p) => p !== productId));
  const clear = () => setIds([]);

  return (
    <WishlistContext.Provider
      value={{ ids, count: ids.length, has, toggle, remove, clear }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = () => useContext(WishlistContext);
